import React from 'react';
import styled, { useTheme } from 'styled-components';

import type { Theme } from '../../../theme/themes/ThemeTypes';
import type { BatteryActivityType } from '../../../utils/socChartUtils';
import { ACTIVITY_LABELS } from '../../../utils/socChartUtils';

type LegendKey = BatteryActivityType | 'solarGeneration';

/**
 * Legend order matches Figma:
 * Row 1: Charge battery from grid, Export to grid, Charge from solar
 * Row 2: Consume from battery, Battery idle, Solar generation
 */
const LEGEND_ORDER: LegendKey[] = ['gridCharge', 'export', 'solarCharge', 'consumption', 'idle', 'solarGeneration'];

const MARKER_SHAPES: Record<
  BatteryActivityType,
  'filledCircle' | 'openCircle' | 'openSquare' | 'filledDiamond' | 'lineOnly'
> = {
  gridCharge: 'filledCircle',
  export: 'filledCircle',
  solarCharge: 'openCircle',
  consumption: 'lineOnly',
  idle: 'openCircle',
};

const LegendContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 8px 16px;
  min-height: calc(2 * 21px + 8px);
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
`;

const LegendLabel = styled.span`
  color: ${(props) => props.theme.colors.black};
  font-size: 14px;
  line-height: 21px;
`;

const RateSquare = styled.div<{ $bgColour: string }>`
  width: 12px;
  height: 12px;
  background-color: ${(props) => props.$bgColour};
  flex-shrink: 0;
`;

const LegendMarkerSvg = ({ children }: { children: React.ReactNode }) => (
  <svg
    width="14"
    height="10"
    // Add a little padding so strokes/shapes at x=0 aren't clipped.
    viewBox="-1 -1 16 12"
    style={{ flexShrink: 0 }}
    aria-hidden="true"
  >
    {children}
  </svg>
);

const ActivityMarker: React.FC<{ activityType: BatteryActivityType; colour: string }> = ({ activityType, colour }) => {
  const shape = MARKER_SHAPES[activityType];
  // With round line caps, the stroke extends slightly past x1.
  // For filled-circle markers this can look like the line "peeks" out to the left.
  const lineStartX = shape === 'filledCircle' ? 1 : 0;

  return (
    <LegendMarkerSvg>
      <line x1={lineStartX} y1="5" x2="14" y2="5" stroke={colour} strokeWidth="2" strokeLinecap="round" />

      {shape === 'filledCircle' && <circle cx="3" cy="5" r="3" fill={colour} />}

      {shape === 'openCircle' && <circle cx="3" cy="5" r="3" fill="white" stroke={colour} strokeWidth="2" />}

      {shape === 'openSquare' && <rect x="0" y="2" width="6" height="6" fill="white" stroke={colour} strokeWidth="2" />}

      {shape === 'filledDiamond' && <polygon points="3,1 6,5 3,9 0,5" fill={colour} />}

      {shape === 'lineOnly' && null}
    </LegendMarkerSvg>
  );
};

const SolarMarker: React.FC<{ colour: string }> = ({ colour }) => (
  <LegendMarkerSvg>
    <line x1="0" y1="5" x2="14" y2="5" stroke={colour} strokeWidth="2" strokeLinecap="round" />
  </LegendMarkerSvg>
);

const DashedLineMarker: React.FC<{ colour: string }> = ({ colour }) => (
  <LegendMarkerSvg>
    <line x1="0" y1="5" x2="14" y2="5" stroke={colour} strokeWidth="2" strokeDasharray="4,3" strokeLinecap="round" />
  </LegendMarkerSvg>
);

interface SocChartLegendProps {
  showBatteryActions?: boolean;
  showTariffRates?: boolean;
  showSolarGeneration?: boolean;
  tariffRateView?: 'import' | 'export';
}

/**
 * Legend component for the SOC plan chart.
 */
const SocChartLegend: React.FC<SocChartLegendProps> = ({
  showBatteryActions = true,
  showTariffRates = true,
  showSolarGeneration = false,
  tariffRateView = 'import',
}) => {
  const theme = useTheme() as Theme;

  const rateLabel = tariffRateView === 'export' ? 'export' : 'import';

  const items = LEGEND_ORDER.filter((key) => {
    if (key === 'solarGeneration') return showSolarGeneration;
    return showBatteryActions;
  });

  return (
    <LegendContainer>
      {!showBatteryActions && (
        <LegendItem key="socLine">
          <SolarMarker colour={theme.colors.secondary} />
          <LegendLabel>Battery state of charge</LegendLabel>
        </LegendItem>
      )}
      {items.map((key) => {
        if (key === 'solarGeneration') {
          return (
            <LegendItem key={key}>
              <SolarMarker colour={theme.colors.solar} />
              <LegendLabel>Solar generation</LegendLabel>
            </LegendItem>
          );
        }
        const activityType = key;
        return (
          <LegendItem key={activityType}>
            <ActivityMarker activityType={activityType} colour={theme.colors.batteryActivity[activityType]} />
            <LegendLabel>{ACTIVITY_LABELS[activityType]}</LegendLabel>
          </LegendItem>
        );
      })}

      {showTariffRates && (
        <>
          <LegendItem key="cheapRates">
            <RateSquare $bgColour={tariffRateView === 'export' ? '#FFE7E2' : '#EBF7F2'} />
            <LegendLabel>Cheap {rateLabel} rates</LegendLabel>
          </LegendItem>
          <LegendItem key="peakRates">
            <RateSquare $bgColour={tariffRateView === 'export' ? '#FFEEA0' : '#E9EDF7'} />
            <LegendLabel>Peak {rateLabel} rates</LegendLabel>
          </LegendItem>
        </>
      )}

      {showBatteryActions && (
        <LegendItem key="forecasted">
          <DashedLineMarker colour={theme.colors.black} />
          <LegendLabel>Forecasted</LegendLabel>
        </LegendItem>
      )}
    </LegendContainer>
  );
};

export default SocChartLegend;
